import styled from '@emotion/styled';
import { forwardRef, useId } from 'react';
import { colors, fonts, radii, breakpoints } from './tokens';

// Figma "Dropdown / Native": label + field with a chevron. It uses a real <select>, so iPad and
// iPhone show the system picker (the light grey popover with a check mark on the selected item
// in the frames), and desktop browsers show their own list.
// This is the ONE select primitive of the session app. `SelectField` (the exams task's copy) is
// now an alias: <Select variant='field'> (48px field, 44px on phones, darker label, "Select").

const Root = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  font-family: ${fonts.family};
  font-size: 12px;
  font-weight: 700;
  line-height: ${({ variant }) => (variant === 'field' ? '20px' : 'normal')};
  color: ${({ accent, variant }) => (accent ? colors.primary : variant === 'field' ? colors.slate[700] : colors.textMuted)};
`;

const Wrap = styled.span`
  position: relative;
  display: block;
  width: 100%;
  svg {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: ${colors.slate[400]};
  }
`;

const Native = styled.select`
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  height: ${({ compact, variant }) => (compact ? 40 : variant === 'field' ? 48 : 50)}px;
  padding: 0 40px 0 12px;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.input}px;
  background: ${({ disabled }) => (disabled ? colors.slate[100] : '#fff')};
  color: ${({ empty, disabled }) => (empty || disabled ? colors.slate[400] : colors.text)};
  font-family: ${fonts.family};
  font-size: 15px;
  font-weight: 400;
  box-sizing: border-box;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  text-overflow: ellipsis;
  &:hover:not(:disabled) {
    border-color: ${colors.slate[300]};
  }
  &:focus {
    outline: 2px solid ${colors.brand[300]};
    border-color: ${colors.brand[400]};
  }
  option {
    color: ${colors.text};
  }
  ${({ variant }) =>
    variant === 'field'
      ? `@media (max-width: ${breakpoints.phone - 1}px) {
    height: 44px;
    font-size: 14px;
  }`
      : ''}
`;

const Chevron = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M6 9l6 6 6-6' />
  </svg>
);

/**
 * Select - Figma "Dropdown / Native".
 * @param label        field label ("Make", "Device", "Select Eye")
 * @param options      [{ value, label, disabled }] or strings; or pass <option> children instead
 * @param value        current value ('' shows the placeholder)
 * @param onChange     (value, event) => void  (gets the value, not the event, first)
 * @param placeholder  text for the empty option (default "Select one…")
 * @param accent       teal label (like the "Patient Device Height" fields)
 * @param compact      40px field instead of 50px
 * @param variant      'default' | 'field' (48px field, 44px on phones, slate-700 label, placeholder "Select")
 * Any other prop (name, id, disabled, required, aria-*) goes to the <select>.
 */
export const Select = forwardRef(
  ({ label, options, value = '', onChange, placeholder, accent, compact, variant = 'default', children, id, style, className, ...rest }, ref) => {
    const autoId = useId();
    const selectId = id || `select-${autoId}`;
    const empty = value === '' || value === null || value === undefined;
    if (placeholder === undefined) placeholder = variant === 'field' ? 'Select' : 'Select one…';
    const list = (options || []).map((o) => (typeof o === 'object' && o !== null ? o : { value: o, label: String(o) }));
    const control = (
      <Wrap>
        <Native
          ref={ref}
          id={selectId}
          value={empty ? '' : String(value)}
          empty={empty}
          compact={compact}
          variant={variant}
          onChange={(e) => onChange?.(e.target.value, e)}
          {...rest}
        >
          {placeholder !== null && (
            <option value='' disabled>
              {placeholder}
            </option>
          )}
          {children ||
            list.map((o) => (
              <option key={String(o.value)} value={String(o.value)} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
        </Native>
        <Chevron />
      </Wrap>
    );
    if (!label)
      return (
        <span style={{ display: 'block', minWidth: 0, ...style }} className={className}>
          {control}
        </span>
      );
    return (
      <Root accent={accent} variant={variant} htmlFor={selectId} style={style} className={className}>
        {label}
        {control}
      </Root>
    );
  },
);
Select.displayName = 'Select';

export default Select;
